import { Ticket } from 'src/app/shared/models/ticket/Ticket';
import { TicketActionTypes } from '../../actions/ticket/ticket.actions';
import { TicketFilter } from 'src/app/shared/models/ticket-filter/TicketFilter';
import { AppState } from 'src/app/app.state';
import { IncidentTicket } from '../../../shared/models/ticket/IncidentTicket';
import { TICKET_LIST_ENTRY_POINT } from 'src/constants/query-params';
import { IInteractionItem } from 'src/app/shared/models/interaction/IInteraction';
import { IResponseMessage } from 'src/app/shared/models/response-message/IResponseMessage';
import { TicketFactory } from 'src/app/shared/models/ticket/TicketFactory';
import { ITicket } from 'src/app/shared/models/ticket/ITicket';
import { IApprovalsTableItem } from 'src/app/shared/components/approvals/approvals.entity';
import { ILicensesTableItem } from 'src/app/shared/components/licenses/licenses.entity';
import {IWatcher} from '../../../shared/models/watcher/IWatcher';

export interface TicketState {
  ticketListEntryPoint?: string; // from which page we went to a ticket list
  ticketFilters: TicketFilter[];
  incidentsTickets?: IncidentTicket[];
  problemsTickets?: Ticket[];
  changeRecordsTickets?: Ticket[];
  requestForChangesTickets?: Ticket[];
  serviceRequestsTickets?: Ticket[];
  tickets?: Ticket[];
  ticketsTotal?: string;
  error?: string;
  loaded: boolean;
  loading: boolean;
  incidentsTicketsTotal: string;
  updatesAvailable: {
    update: boolean;
    updateinteraction: boolean;
  };
  currentTicketLoading: boolean;
  currentTicketLoaded: boolean;
  changeRecordsTicketLoading: boolean;
  changeRecordsTicketLoaded: boolean;
  serviceRequestsTicketsTotal: string;
  problemsTicketsTotal: string;
  changeRecordsTicketsTotal: string;
  changeRecordsTicket: any;
  requestForChangesTicketsTotal: string;
  requestForChangesTicket: any;
  incidentsTicket: any;
  currentTicket: ITicket;
  currentTicketApprovalsLoading: boolean;
  currentTicketApprovals: { list: IApprovalsTableItem[]; approved: boolean; note: string };
  problemsTicket: any;
  serviceRequestsTicket: any;
  approvals: [];
  approvalsTotal: string;
  approvalsLoading: boolean;
  interactions: IInteractionItem[];
  interactionsTotal: string;
  interactionsLoading: boolean;
  postInteractionsError: IResponseMessage;
  uploadFileError: IResponseMessage;
  postInteractionsSuccess: IInteractionItem;
  orderItems: any[];
  ordersLoadingStatus: boolean;

  currentTicketLicensesLoading: boolean;
  currentTicketLicenses: { list: ILicensesTableItem[] };
  licenses: [];
  licensesTotal: string;
  licensesLoading: boolean;

  postWatchersSuccess: IWatcher;
  postWatchersError: IResponseMessage;
}

const initialState: TicketState = {
  ticketListEntryPoint: null,
  ticketFilters: [],
  incidentsTickets: [],
  problemsTickets: [],
  changeRecordsTickets: [],
  requestForChangesTickets: [],
  serviceRequestsTickets: [],
  serviceRequestsTicket: null,
  currentTicketLoading: false,
  currentTicketLoaded: false,
  changeRecordsTicketLoading: false,
  changeRecordsTicketLoaded: false,
  updatesAvailable: {
    update: false,
    updateinteraction: false
  },
  loaded: false,
  loading: false,
  incidentsTicketsTotal: null,
  serviceRequestsTicketsTotal: null,
  problemsTicketsTotal: null,
  incidentsTicket: null,
  changeRecordsTicketsTotal: null,
  changeRecordsTicket: null,
  requestForChangesTicketsTotal: null,
  requestForChangesTicket: null,
  problemsTicket: null,
  approvals: [],
  approvalsTotal: null,
  approvalsLoading: false,
  interactions: [],
  interactionsTotal: null,
  interactionsLoading: false,
  postInteractionsError: null,
  uploadFileError: null,
  postInteractionsSuccess: null,
  currentTicket: null,
  currentTicketApprovals: null,
  currentTicketApprovalsLoading: false,
  orderItems: [],
  ordersLoadingStatus: false,

  licenses: [],
  licensesTotal: null,
  licensesLoading: false,
  currentTicketLicenses: null,
  currentTicketLicensesLoading: false,
  postWatchersSuccess: null,
  postWatchersError: null,
};

export function ticketReducer(
  state: TicketState = initialState,
  action: any
): TicketState {
  switch (action.type) {
    case TicketActionTypes.SEARCH:
      return {
        ...state,
        loading: true
      };
    case TicketActionTypes.SEARCH_CHANGE_RECORDS_ERROR:
      return {
        ...state,
        changeRecordsTickets: [],
        error: action.error.message,
        loading: false,
        loaded: true
      };
    case TicketActionTypes.SEARCH_SUCCESS:
      return {
        ...state,
        tickets: action.tickets,
        ticketsTotal: action.totalCount,
        loading: false,
        loaded: true
      };
    case TicketActionTypes.CANCEL_SEARCH:
      return {
        ...state,
        loading: false
      };
    case TicketActionTypes.CLEAR_TICKETS:
      return {
        ...state,
        tickets: [],
        ticketsTotal: '0',
        loading: false
      };
    case TicketActionTypes.CLEAR_TICKET:
      return {
        ...state,
        currentTicket: null
      };
    case TicketActionTypes.GET_UPDATES_AVAILABLE_SUCCESS:
      return {
        ...state,
        updatesAvailable: {
          ...state.updatesAvailable,
          [action.updatesType]: action.available
        }
      };
    case TicketActionTypes.SEARCH_CHANGE_RECORDS_SUCCESS:
      return {
        ...state,
        changeRecordsTickets: action.tickets,
        loading: false,
        loaded: true,
        changeRecordsTicketsTotal: action.totalCount
      };
    case TicketActionTypes.CLEAR_CHANGE_RECORDS_TICKETS:
      return {
        ...state,
        changeRecordsTickets: [],
        loading: false,
        loaded: false,
        changeRecordsTicketsTotal: null
      };

    case TicketActionTypes.GET_TICKET_LIST_ENTRY_POINT_SUCCESS:
      return {
        ...state,
        ticketListEntryPoint: action[TICKET_LIST_ENTRY_POINT],
        loading: false,
        loaded: true
      };

    case TicketActionTypes.GET_TICKET_LIST_ENTRY_POINT_ERROR:
      return {
        ...state,
        ticketListEntryPoint: null,
        loading: false,
        loaded: true
      };

    case TicketActionTypes.GET_TICKET:
        return {
          ...state,
          currentTicketLoading: true,
          currentTicketLoaded: false,
        };

    case TicketActionTypes.GET_TICKET_SUCCESS:
      return {
        ...state,
        currentTicket: TicketFactory.createTicket(
          action.ticket,
          action.statuses,
          action.prioritys
        ),
        changeRecordsTicket: null,
        requestForChangesTicket: null,
        currentTicketLoading: false,
        currentTicketLoaded: true,
      };

    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET:
        return {
          ...state,
          changeRecordsTicketLoading: true,
          changeRecordsTicketLoaded: false,
        };

    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET_SUCCESS:
      return {
        ...state,
        currentTicket: null,
        changeRecordsTicket: TicketFactory.createTicket(
          action.ticket,
          action.statuses,
          action.prioritys
        ),
        requestForChangesTicket: null,
        changeRecordsTicketLoading: false,
        changeRecordsTicketLoaded: true,
      };

    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET_ASSESSMENT_SUCCESS:
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          assessment: {
            business: action.assessment
              ? action.assessment.justification
              : null,
            effect: action.assessment
              ? action.assessment.effect_not_impl
              : null,
            risk: action.assessment ? action.assessment.analysis : null
          }
        }
      };

    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET_PLANNING_SUCCESS:
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          planning: {
            validation: action.planning ? action.planning.validation : null,
            implementation: action.planning
              ? action.planning.implementation
              : null,
            test: action.planning ? action.planning.test : null,
            backout: action.planning ? action.planning.backout : null
          }
        }
      };

    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET_SCHEDULING_SUCCESS:
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          scheduling: {
            planStart: action.scheduling ? action.scheduling.plan_start : null,
            planEnd: action.scheduling ? action.scheduling.plan_end : null,
            conflicted:
              action.scheduling && action.scheduling.conflicted === 'yes'
          }
        }
      };

    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET_AFFECTED_SUCCESS:
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          affected: {
            ...state.changeRecordsTicket.affected,
            list: action.affected
          }
        }
      };

    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET_IMPLEMENTATION_SUCCESS:
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          implementation: {
            startDate: action.implementation
              ? action.implementation.start_date
              : null,
            endDate: action.implementation
              ? action.implementation.end_date
              : null,
            note: action.implementation ? action.implementation.implementation_note : null,
            isSuccessful: action.implementation
              ? parseInt(action.implementation.is_successful, 10)
              : null,
            validation: action.implementation
              ? action.implementation.validation_results
              : null,
            negativeImpact: action.implementation
              ? parseInt(action.implementation.negative_impact, 10)
              : null,
            impactDescription: action.implementation
              ? action.implementation.impact_description
              : null,
            backoutPerformed: action.implementation
              ? parseInt(action.implementation.backout_performed, 10)
              : null,
            backoutDetails: action.implementation
              ? action.implementation.backout_details
              : null,
            obligatory: action.implementation
              ? parseInt(action.implementation.obligatory_review, 10)
              : null
          }
        }
      };
    case TicketActionTypes.GET_TICKET_APPROVALS: {
      return {
        ...state,
        currentTicketApprovalsLoading: true
      };
    }
    case TicketActionTypes.GET_TICKET_APPROVALS_SUCCESS: {
      return {
        ...state,
        currentTicketApprovalsLoading: false,
        currentTicketApprovals: {
          approved: action.approvals && action.approvals.approved === '1',
          list: action.approvals ? action.approvals.approvals : [],
          note: action.approvals ? action.approvals.note : ''
        }
      };
    }
    case TicketActionTypes.GET_CHANGE_RECORDS_TICKET_APPROVALS_SUCCESS:
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          approvals: {
            approved: action.approvals
              ? parseInt(action.approvals.approved, 10)
              : null,
            list: action.approvals ? action.approvals.approvals : null,
            note: action.approvals ? action.approvals.note : null
          }
        }
      };

    case TicketActionTypes.GET_CHANGE_TASKS_FOR_CHANGE_RECORDS_TICKET_SUCCESS:
      action.changeTasks.relations.map(
        item => (item.state = action.changeTasks.state.name)
      );
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          changeTasks: {
            list: action.changeTasks.relations
          }
        }
      };
    case TicketActionTypes.GET_APPROVALS:
      return {
        ...state,
        approvalsLoading: true
      };
    case TicketActionTypes.GET_APPROVALS_SUCCESS:
      return {
        ...state,
        approvalsLoading: false,
        approvals: action.approvals.map(item => ({
          id: item.record_id,
          description: item.name,
          title: item.record_id,
          date: item.creation_time,
          url: 'dashboard/tickets/approvals'
        })),
        approvalsTotal: action.totalCount
      };
    case TicketActionTypes.GET_TICKET_INTERACTIONS:
      return {
        ...state,
        interactionsLoading: true
      };
    case TicketActionTypes.CLEAR_INTERACTIONS:
      return {
        ...state,
        interactions: []
      };
    case TicketActionTypes.CLEAR_CHANGE_RECORDS_TICKET:
      return {
        ...state,
        changeRecordsTicket: null
      };
    case TicketActionTypes.CLEAR_APPROVALS:
      return {
        ...state,
        approvals: []
      };

    case TicketActionTypes.GET_TICKET_INTERACTIONS_SUCCESS:
      return {
        ...state,
        interactionsLoading: false,
        interactions: action.interactions.map(item => ({
          id: item.id,
          title: item.title,
          message: item.message || '',
          date: item.creation_time,
          created_by: item.created_by,
          incomingFiles: item.incomingFiles
        })),
        interactionsTotal: action.totalCount
      };

    case TicketActionTypes.POST_TICKET_INTERACTION_ERROR:
      return {
        ...state,
        interactionsLoading: false,
        postInteractionsError: action.error
      };

    case TicketActionTypes.POST_TICKET_INTERACTION_SUCCESS:
      let ticket = 'currentTicket';

      if (state.changeRecordsTicket) {
        ticket = 'changeRecordsTicket';
      } else if (state.requestForChangesTicket) {
        ticket = 'requestForChangesTicket';
      }

      return {
        ...state,
        [ticket]: {
          ...state[ticket],
          relations: [
            ...action.relations
              .filter(item => item.type === 'TOPIC_REL')
              .map(item => ({
                ...item,
                related_record: {
                  ...item.related_record,
                  creation_time: action.creation_time,
                  created_by: action.created_by,
                  message: action.message
                }
              })),
            ...state[ticket].relations
          ]
        },
        interactionsLoading: false,
        postInteractionsSuccess: action
      };

    case TicketActionTypes.SET_ORDER_ITEMS:
      return {
        ...state,
        orderItems: action.orders
      };
    case TicketActionTypes.ORDERS_LOADING_STATUS:
      return {
        ...state,
        ordersLoadingStatus: action.isLoading
      };

    // rfc
    case TicketActionTypes.SEARCH_REQUEST_FOR_CHANGES_ERROR:
      return {
        ...state,
        requestForChangesTickets: [],
        error: action.error.message,
        loading: false,
        loaded: true
      };
    case TicketActionTypes.CLEAR_REQUEST_FOR_CHANGES_TICKET:
      return {
        ...state,
        requestForChangesTicket: null
      };
    case TicketActionTypes.SEARCH_REQUEST_FOR_CHANGES_SUCCESS:
      return {
        ...state,
        requestForChangesTickets: action.tickets,
        loading: false,
        loaded: true,
        requestForChangesTicketsTotal: action.totalCount
      };

    case TicketActionTypes.CLEAR_REQUEST_FOR_CHANGES_TICKETS:
      return {
        ...state,
        requestForChangesTickets: [],
        loading: false,
        loaded: false,
        requestForChangesTicketsTotal: null
      };
    case TicketActionTypes.GET_REQUEST_FOR_CHANGES_TICKET_SUCCESS:
      return {
        ...state,
        currentTicket: null,
        changeRecordsTicket: null,
        requestForChangesTicket: TicketFactory.createTicket(
          action.ticket,
          action.statuses,
          action.prioritys
        )
      };

    case TicketActionTypes.GET_REQUEST_FOR_CHANGES_TICKET_APPROVALS_SUCCESS:
      return {
        ...state,
        requestForChangesTicket: {
          ...state.requestForChangesTicket,
          approvals: {
            approved: action.approvals
              ? parseInt(action.approvals.approved, 10)
              : null,
            list: action.approvals ? action.approvals.approvals : null,
            note: action.approvals ? action.approvals.note : null
          }
        }
      };
// licenses
    case TicketActionTypes.GET_TICKET_LICENSES: {
      // console.log("reducer GET_TICKET_LICENSES", action.type);
      return {
        ...state,
        currentTicketLicensesLoading: true,
      };
    }
    case TicketActionTypes.GET_TICKET_LICENSES_SUCCESS: {
      // console.log("reducer GET_TICKET_LICENSES_SUCCESS", action.type);
      return {
        ...state,
        currentTicketLicensesLoading: false,
        currentTicketLicenses: {
          list: action.licenses ? action.licenses : []
        }
      };
    }
    case TicketActionTypes.GET_REQUEST_FOR_CHANGES_TICKET_LICENSES_SUCCESS:
      // console.log("reducer GET_REQUEST_FOR_CHANGES_TICKET_LICENSES_SUCCESS", action.type);
      return {
        ...state,
        changeRecordsTicket: {
          ...state.changeRecordsTicket,
          licenses: {
            list: action.Licenses ? action.licenses : null,
            note: null
          }
        }
      };
    case TicketActionTypes.GET_LICENSES:
      // console.log('reducer GET_LICENSES', action.type);
      return {
        ...state,
        licensesLoading: true
      };
    case TicketActionTypes.GET_LICENSES_SUCCESS:
      // console.log('reducer GET_LICENSES_SUCCESS', action);
      return {
        ...state,
        licensesLoading: false,
        currentTicketLicensesLoading: false,
        currentTicketLicenses: {
          list: action.licenses ? action.licenses.map(item => ({
            license_tag: item.license_tag.license_tag,
            short_license_desc: item.license_tag.short_license_desc,
            base_lic_price: item.base_lic_price,
            contracted_lic_price: item.contracted_lic_price,
            maint_base_price: item.maint_base_price,
          })) : []
        },
        licenses: action.licenses.map(item => ({
          license_tag: item.license_tag.license_tag,
          short_license_desc: item.license_tag.short_license_desc,
          base_lic_price: item.base_lic_price,
          contracted_lic_price: item.contracted_lic_price,
          maint_base_price: item.maint_base_price,
        })),
        licensesTotal: action.totalCount
      };
    case TicketActionTypes.CLEAR_LICENSES:
      return {
        ...state,
        licenses: []
      };

    default:
      // console.log('reducer default', action.type);
      return state;
  }
}

export const getTicketState = (state: AppState) => state.ticket;

export const getTicketFilters = (state: TicketState) => state.ticketFilters;
export const getTicketListEntryPoint = (state: TicketState) => state.ticketListEntryPoint;
export const getChangeRecordsTicket = (state: TicketState) => state.changeRecordsTicket;
export const getChangeRecordsTicketsList = (state: TicketState) => state.changeRecordsTickets;
export const getChangeRecordsTicketsListTotal = (state: TicketState) => state.changeRecordsTicketsTotal;
export const getCurrentTicketsLoading = (state: TicketState) => state.currentTicketLoading;
export const getCurrentTicketsLoaded = (state: TicketState) => state.currentTicketLoaded;
export const getChangeRecordsTicketsLoading = (state: TicketState) => state.changeRecordsTicketLoading;
export const getChangeRecordsTicketsLoaded = (state: TicketState) => state.changeRecordsTicketLoaded;
export const getProblemsTicket = (state: TicketState) => state.problemsTicket;
export const getCurrentTicket = (state: TicketState) => state.currentTicket;
export const getPendingApprovals = (state: TicketState) => state.approvals;
export const getPendingApprovalsTotalCount = (state: TicketState) => state.approvalsTotal;
export const getPendingApprovalsLoading = (state: TicketState) => state.approvalsLoading;
export const getInteractions = (state: TicketState) => state.interactions;
export const getInteractionsLoading = (state: TicketState) => state.interactionsLoading;
export const getInteractionsTotalCount = (state: TicketState) => state.interactionsTotal;
export const getPostInteractionsError = (state: TicketState) => state.postInteractionsError;
export const getUploadFileError = (state: TicketState) => state.uploadFileError;
export const getPostInteractionsSuccess = (state: TicketState) => state.postInteractionsSuccess;
export const getTicketsList = (state: TicketState) => state.tickets;
export const getTicketsListTotal = (state: TicketState) => state.ticketsTotal;
export const getUpdatesAvailable = (state: TicketState) => state.updatesAvailable.update;
export const getNewMessagesAvailable = (state: TicketState) => state.updatesAvailable.updateinteraction;
export const getTicketsLoading = (state: TicketState) => state.loading;
export const getCurrentTicketApprovalsLoading = (state: TicketState) => state.currentTicketApprovalsLoading;
export const getCurrentTicketApprovals = (state: TicketState) => state.currentTicketApprovals;
export const getTicketOrders = (state: TicketState) => state.orderItems;
export const getOrdersLoadingStatus = (state: TicketState) => state.ordersLoadingStatus;
export const getRequestForChangesTicket = (state: TicketState) => state.requestForChangesTicket;
export const getRequestForChangesTicketsList = (state: TicketState) => state.requestForChangesTickets;
export const getRequestForChangesTicketsListTotal = (state: TicketState) => state.requestForChangesTicketsTotal;
// licenses
export const getCurrentTicketLicensesLoading = (state: TicketState) => state.currentTicketLicensesLoading;
export const getCurrentTicketLicenses = (state: TicketState) => state.currentTicketLicenses;
export const getPendingLicenses = (state: TicketState) => state.licenses;
export const getPendingLicensesTotalCount = (state: TicketState) => state.licensesTotal;
export const getPendingLicensesLoading = (state: TicketState) => state.licensesLoading;
// watchers
export const getPostWatchersSuccess = (state: TicketState) => state.postWatchersSuccess;
export const getPostWatchersError = (state: TicketState) => state.postWatchersError;
